/**
 * @zendir/ui - Theme Provider
 *
 * Enterprise-grade theming system supporting Astro UX Design System and Zendir themes.
 * Full Tier 3 compliance with official Astro tokens (dark + light themes).
 *
 * Features:
 * - Multiple theme variants (Zen Hybrid default, astro, purple-hue, transparent)
 * - Light and dark modes
 * - Animation tokens with reduced-motion support
 * - Focus ring styles for accessibility
 * - CSS variable injection
 * - Local storage persistence
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import {
  astroThemeDark,
  astroThemeLight,
  hybridTheme,
  purpleHueTheme,
  nebulaTheme,
  transparentTheme,
  transparentMinimalTheme,
} from "../../tokens/themes";
import {
  computeBorderTokens,
  type ThemeVariant,
  type ThemeMode,
  type ThemeColors,
  type ThemeTokens,
  type ThemeAnimation,
  type ThemeFocus,
  type LayoutTokens,
  type BorderTokens,
} from "./types";

// Theme type shapes and derived-token helpers live in ./types so the token
// build (which imports the theme definitions) never pulls in this provider.
// Re-exported here to preserve the long-standing `@zendir/ui` import surface.
export type {
  ThemeVariant,
  ThemeMode,
  ThemeColors,
  ThemeTokens,
  ThemeAnimation,
  ThemeFocus,
  LayoutTokens,
  BorderTokens,
};

function adjustHexBrightness(hex: string, factor: number): string {
  const normalized = hex.replace("#", "").trim();
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return hex;
  }

  const clamp = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)));
  const r = clamp(parseInt(expanded.slice(0, 2), 16) * factor);
  const g = clamp(parseInt(expanded.slice(2, 4), 16) * factor);
  const b = clamp(parseInt(expanded.slice(4, 6), 16) * factor);

  const toHex = (value: number) => value.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// ============================================================================
// Theme Context
// ============================================================================

export interface ThemeContextValue {
  theme: ThemeVariant;
  mode: ThemeMode;
  tokens: ThemeTokens;
  prefersReducedMotion: boolean;
  /** Custom accent color override (null = use theme default) */
  accentColor: string | null;
  setTheme: (theme: ThemeVariant) => void;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  /** Override the accent color for the current theme */
  setAccentColor: (color: string | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme variant */
  theme?: ThemeVariant;
  /** Alias for `theme` */
  defaultVariant?: ThemeVariant;
  /** Default color mode */
  mode?: ThemeMode;
  /** Alias for `mode` */
  defaultMode?: ThemeMode;
  /** Persist preferences to localStorage */
  persist?: boolean;
}

// ============================================================================
// Scrollbar Styling System
// ============================================================================
// Generates global CSS for thin, elegant, theme-aware scrollbars.
//
// Features:
// - Ultra-thin (6px) scrollbar tracks with soft border radius
// - Accent-colored thumb with hover/active brightness shifts
// - Semi-transparent track that blends into the surface
// - Auto-hide behaviour on modern browsers (overlay mode)
// - Firefox support via scrollbar-width / scrollbar-color
// - WCAG 2.1 AA compliant: sufficient contrast, respects reduced-motion
// - Smooth transitions (disabled when prefers-reduced-motion)
// - Scoped .zendir-scroll utility class for individual containers
// ============================================================================

/**
 * Converts a hex color to an rgba string helper.
 * Accepts 3/6/8-char hex codes (#rgb, #rrggbb, #rrggbbaa).
 */
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  let r: number, g: number, b: number;
  if (clean.length === 3) {
    r = parseInt(clean[0] + clean[0], 16);
    g = parseInt(clean[1] + clean[1], 16);
    b = parseInt(clean[2] + clean[2], 16);
  } else {
    r = parseInt(clean.slice(0, 2), 16);
    g = parseInt(clean.slice(2, 4), 16);
    b = parseInt(clean.slice(4, 6), 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildScrollbarCSS(
  tokens: ThemeTokens,
  mode: ThemeMode,
  reducedMotion: boolean,
): string {
  const accent = tokens.colors.accent.primary;
  const _surface = tokens.colors.background.surface; // eslint-disable-line @typescript-eslint/no-unused-vars -- reserved for future scrollbar track customisation
  const _base = tokens.colors.background.base; // eslint-disable-line @typescript-eslint/no-unused-vars -- reserved for future scrollbar corner styling
  const isLight = mode === "light";

  // Thumb colors: accent-tinted, low opacity at rest, stronger on hover/active
  const thumbRest = isLight ? hexToRgba(accent, 0.28) : hexToRgba(accent, 0.35);
  const thumbHover = isLight
    ? hexToRgba(accent, 0.48)
    : hexToRgba(accent, 0.55);
  const thumbActive = isLight
    ? hexToRgba(accent, 0.64)
    : hexToRgba(accent, 0.72);

  // Track: nearly invisible surface blend
  const trackColor = isLight
    ? hexToRgba(accent, 0.04)
    : hexToRgba(accent, 0.06);
  const trackHover = isLight ? hexToRgba(accent, 0.08) : hexToRgba(accent, 0.1);

  const transition = reducedMotion
    ? "none"
    : "background-color 200ms ease, width 200ms ease, opacity 200ms ease";
  const thumbRadius = "9999px"; // pill shape

  // Firefox colors (two-value syntax: thumb track)
  const ffThumb = thumbRest;
  const ffTrack = trackColor;

  return `
/* ═══════════════════════════════════════════════════════════════════════════
   Zendir Scrollbar System — Thin · Elegant · Theme-Connected
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Firefox ────────────────────────────────────────────────────────────── */
*,
.zendir-scroll {
  scrollbar-width: thin;
  scrollbar-color: ${ffThumb} ${ffTrack};
}

/* ── Webkit / Blink (Chrome, Edge, Safari, Opera) ───────────────────────── */

/* Global thin scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
  background: transparent;
}

/* Track — nearly invisible, subtle on hover */
::-webkit-scrollbar-track {
  background: ${trackColor};
  border-radius: ${thumbRadius};
  margin: 2px;
}
::-webkit-scrollbar-track:hover {
  background: ${trackHover};
}

/* Thumb — accent-tinted pill */
::-webkit-scrollbar-thumb {
  background: ${thumbRest};
  border-radius: ${thumbRadius};
  border: 1px solid transparent;
  background-clip: padding-box;
  transition: ${transition};
  min-height: 32px;
}
::-webkit-scrollbar-thumb:hover {
  background: ${thumbHover};
  border-color: transparent;
  background-clip: padding-box;
}
::-webkit-scrollbar-thumb:active {
  background: ${thumbActive};
  border-color: transparent;
  background-clip: padding-box;
}

/* Corner — where horizontal & vertical scrollbars meet */
::-webkit-scrollbar-corner {
  background: transparent;
}

/* ── Utility class: zendir-scroll ────────────────────────────────────────
   Apply to individual containers for scoped thin scrollbar.
   Also enables overlay behaviour where supported.
   e.g. <div className="zendir-scroll" style={{overflow:'auto'}}>…</div>
   ─────────────────────────────────────────────────────────────────────── */
.zendir-scroll {
  overflow: auto;
  overflow: overlay; /* Chrome / Edge: auto-hide scrollbar */
}
.zendir-scroll::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
.zendir-scroll::-webkit-scrollbar-track {
  background: transparent;
  border-radius: ${thumbRadius};
}
.zendir-scroll::-webkit-scrollbar-thumb {
  background: ${thumbRest};
  border-radius: ${thumbRadius};
  transition: ${transition};
}
.zendir-scroll::-webkit-scrollbar-thumb:hover {
  background: ${thumbHover};
}
.zendir-scroll::-webkit-scrollbar-thumb:active {
  background: ${thumbActive};
}

/* ── Utility: zendir-scroll--hidden ─────────────────────────────────────
   Hides scrollbar visually but keeps scroll behaviour (accessible).
   Content remains scrollable via mouse wheel / touch / keyboard.
   ─────────────────────────────────────────────────────────────────────── */
.zendir-scroll--hidden {
  overflow: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.zendir-scroll--hidden::-webkit-scrollbar {
  display: none;
}

/* ── Focus-visible: keyboard users can still scroll with focus ─────────── */
.zendir-scroll:focus-visible {
  outline: 2px solid ${accent};
  outline-offset: -2px;
  border-radius: ${tokens.borderRadius.md};
}

/* ── Reduced Motion: disable scrollbar transitions ─────────────────────── */
@media (prefers-reduced-motion: reduce) {
  ::-webkit-scrollbar-thumb,
  .zendir-scroll::-webkit-scrollbar-thumb {
    transition: none !important;
  }
}

/* ── High Contrast Mode: ensure scrollbar is visible ───────────────────── */
@media (forced-colors: active) {
  ::-webkit-scrollbar-thumb {
    background: ButtonText !important;
  }
  ::-webkit-scrollbar-track {
    background: Canvas !important;
  }
}
`.trim();
}

/**
 * useScrollbarStyles — returns inline React.CSSProperties for a
 * scrollable container that matches the current Zendir theme.
 *
 * Use when you need programmatic control rather than the
 * `zendir-scroll` CSS class.
 *
 * @example
 * ```tsx
 * const scrollStyle = useScrollbarStyles();
 * <div style={{ maxHeight: 400, ...scrollStyle }}>…</div>
 * ```
 */
export function useScrollbarStyles(): React.CSSProperties {
  const { tokens, mode } = useTheme();
  return useMemo((): React.CSSProperties => {
    const accent = tokens.colors.accent.primary;
    const isLight = mode === "light";
    const thumbColor = isLight
      ? hexToRgba(accent, 0.28)
      : hexToRgba(accent, 0.35);
    const trackColor = isLight
      ? hexToRgba(accent, 0.04)
      : hexToRgba(accent, 0.06);
    return {
      // Firefox
      scrollbarWidth: "thin" as const,
      scrollbarColor: `${thumbColor} ${trackColor}`,
      overflow: "auto",
    };
  }, [tokens, mode]);
}

/**
 * ThemeProvider - Enterprise theme context provider
 *
 * Provides theme tokens, mode switching, and accessibility features
 * to all child components.
 *
 * @example
 * ```tsx
 * <ThemeProvider theme="hybrid" mode="dark">  // Zen (Hybrid) is the default
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
  theme: themeProp,
  defaultVariant,
  mode: modeProp,
  defaultMode: defaultModeProp,
  persist = true,
}: ThemeProviderProps): React.ReactElement {
  const defaultTheme = themeProp ?? defaultVariant ?? "hybrid";
  const defaultMode = modeProp ?? defaultModeProp ?? "dark";
  // Initialize theme from localStorage or defaults
  const [theme, setThemeState] = useState<ThemeVariant>(() => {
    if (persist && typeof window !== "undefined") {
      const saved = localStorage.getItem("zendir-ui-theme");
      if (
        saved === "astro" ||
        saved === "zendir" ||
        saved === "hybrid" ||
        saved === "nebula" ||
        saved === "purple-hue" ||
        saved === "transparent" ||
        saved === "transparent-bold" ||
        saved === "transparent-minimal"
      ) {
        return saved as ThemeVariant;
      }
    }
    return defaultTheme;
  });

  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (persist && typeof window !== "undefined") {
      const saved = localStorage.getItem("zendir-ui-mode");
      if (saved === "light" || saved === "dark") {
        return saved;
      }
    }
    return defaultMode;
  });

  // Detect reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) =>
      setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // Theme setters
  const setTheme = (newTheme: ThemeVariant) => {
    setThemeState(newTheme);
    if (persist && typeof window !== "undefined") {
      localStorage.setItem("zendir-ui-theme", newTheme);
    }
  };

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    if (persist && typeof window !== "undefined") {
      localStorage.setItem("zendir-ui-mode", newMode);
    }
  };

  const toggleMode = () => {
    setMode(mode === "dark" ? "light" : "dark");
  };

  // Accent color override
  const [accentColor, setAccentColorState] = useState<string | null>(() => {
    if (persist && typeof window !== "undefined") {
      return localStorage.getItem("zendir-ui-accent") || null;
    }
    return null;
  });

  const setAccentColor = (color: string | null) => {
    setAccentColorState(color);
    if (persist && typeof window !== "undefined") {
      if (color) {
        localStorage.setItem("zendir-ui-accent", color);
      } else {
        localStorage.removeItem("zendir-ui-accent");
      }
    }
  };

  // Get theme tokens (card border styles computed so all cards use theme-driven borders)
  const tokens = useMemo((): ThemeTokens => {
    // If light mode is selected, always use Astro Light theme (unified light mode)
    // regardless of the selected theme variant
    const base =
      mode === "light"
        ? astroThemeLight
        : theme === "astro"
          ? astroThemeDark
          : theme === "hybrid"
            ? hybridTheme
            : theme === "nebula"
              ? nebulaTheme
              : theme === "transparent" || theme === "transparent-bold"
                ? transparentTheme
                : theme === "transparent-minimal"
                  ? transparentMinimalTheme
                  : purpleHueTheme;
    const isTransparent =
      theme === "transparent" ||
      theme === "transparent-bold" ||
      theme === "transparent-minimal";
    const isMinimal = theme === "transparent-minimal";

    // For transparent-minimal: use visible accent-colored borders.
    // For other transparent themes: use subtle fadedBoxShadow.
    // Accent overrides are intentionally scoped to transparent variants only,
    // so switching to Astro/Hybrid/Purple-Hue restores each theme's native accent.
    const resolvedAccent =
      isTransparent && accentColor
        ? {
            primary: accentColor,
            secondary: adjustHexBrightness(accentColor, 1.2),
            tertiary: adjustHexBrightness(accentColor, 0.85),
          }
        : base.colors.accent;

    const accentPrimary = resolvedAccent.primary;
    const accentBorderMuted = `${accentPrimary}40`; // 25% opacity
    const accentBorderDefault = `${accentPrimary}66`; // 40% opacity
    const accentFadedBoxShadow = `inset 0 1px 0 ${hexToRgba(accentPrimary, 0.12)}, inset 0 -1px 0 rgba(0, 0, 0, 0.05), inset 1px 0 0 ${hexToRgba(accentPrimary, 0.06)}, inset -1px 0 0 ${hexToRgba(accentPrimary, 0.06)}`;

    const cardStyle = isMinimal
      ? { border: `1px solid ${accentBorderMuted}` }
      : isTransparent
        ? { border: "none" as const, boxShadow: accentFadedBoxShadow }
        : { border: `1px solid ${base.colors.border.muted}` };
    const cardStyleDashed = isMinimal
      ? { border: `1px dashed ${accentBorderMuted}` }
      : isTransparent
        ? { border: "none" as const, boxShadow: accentFadedBoxShadow }
        : { border: `1px dashed ${base.colors.border.muted}` };
    const cardStyleDefault = isMinimal
      ? { border: `1px solid ${accentBorderDefault}` }
      : isTransparent
        ? { border: "none" as const, boxShadow: accentFadedBoxShadow }
        : { border: `1px solid ${base.colors.border.default}` };
    // Compute border tokens from the resolved theme colors
    const resolvedColors: ThemeColors = {
      ...base.colors,
      accent: resolvedAccent,
      border: {
        ...base.colors.border,
        cardStyle,
        cardStyleDashed,
        cardStyleDefault,
      },
    };

    return {
      ...base,
      colors: resolvedColors,
      borders: computeBorderTokens(resolvedColors),
    } as ThemeTokens;
  }, [theme, mode, accentColor]);

  // Apply CSS variables to document
  useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    const {
      colors,
      typography,
      spacing,
      borderRadius,
      animation,
      focus,
      shadows,
    } = tokens;

    // Theme attributes. tokens.css keys its variable blocks on the VARIANT
    // via [data-theme="<variant>"] and the mode via [data-mode="light"], so
    // data-theme must carry the variant and data-mode the light/dark mode —
    // otherwise the static --zendir-* CSS variables never leave the default
    // (hybrid) block. data-variant is retained for backward compatibility.
    root.setAttribute("data-theme", theme);
    root.setAttribute("data-mode", mode);
    root.setAttribute("data-variant", theme);

    // Background colors
    root.style.setProperty("--color-background-base", colors.background.base);
    root.style.setProperty(
      "--color-background-surface",
      colors.background.surface,
    );
    root.style.setProperty(
      "--color-background-elevated",
      colors.background.elevated,
    );
    root.style.setProperty(
      "--color-background-overlay",
      colors.background.overlay,
    );

    // Border colors
    root.style.setProperty("--color-border", colors.border.default);
    root.style.setProperty("--color-border-muted", colors.border.muted);
    root.style.setProperty("--color-border-focus", colors.border.focus);

    // Text colors
    root.style.setProperty("--color-text-primary", colors.text.primary);
    root.style.setProperty("--color-text-secondary", colors.text.secondary);
    root.style.setProperty("--color-text-tertiary", colors.text.tertiary);
    root.style.setProperty("--color-text-inverse", colors.text.inverse);

    // Status colors
    root.style.setProperty("--color-status-normal", colors.status.normal);
    root.style.setProperty("--color-status-standby", colors.status.standby);
    root.style.setProperty("--color-status-caution", colors.status.caution);
    root.style.setProperty("--color-status-serious", colors.status.serious);
    root.style.setProperty("--color-status-critical", colors.status.critical);
    root.style.setProperty("--color-status-off", colors.status.off);

    // Interactive colors
    root.style.setProperty(
      "--color-interactive-default",
      colors.interactive.default,
    );
    root.style.setProperty(
      "--color-interactive-hover",
      colors.interactive.hover,
    );
    root.style.setProperty(
      "--color-interactive-active",
      colors.interactive.active,
    );
    root.style.setProperty(
      "--color-interactive-disabled",
      colors.interactive.disabled,
    );

    // Accent colors
    root.style.setProperty("--color-accent-primary", colors.accent.primary);
    root.style.setProperty("--color-accent-secondary", colors.accent.secondary);
    root.style.setProperty("--color-accent-tertiary", colors.accent.tertiary);

    // Typography
    root.style.setProperty(
      "--font-family-heading",
      typography.fontFamily.heading || typography.fontFamily.primary,
    );
    root.style.setProperty(
      "--font-family-primary",
      typography.fontFamily.primary,
    );
    root.style.setProperty("--font-family-mono", typography.fontFamily.mono);

    // Spacing (full scale so CSS and layout can pull from SDK)
    root.style.setProperty("--spacing-xxs", spacing.xxs);
    root.style.setProperty("--spacing-xs", spacing.xs);
    root.style.setProperty("--spacing-sm", spacing.sm);
    root.style.setProperty("--spacing-smd", spacing.smd);
    root.style.setProperty("--spacing-md", spacing.md);
    root.style.setProperty("--spacing-mdl", spacing.mdl);
    root.style.setProperty("--spacing-lg", spacing.lg);
    root.style.setProperty("--spacing-xl", spacing.xl);
    root.style.setProperty("--spacing-xxl", spacing.xxl);

    // Border radius
    root.style.setProperty("--radius-sm", borderRadius.sm);
    root.style.setProperty("--radius-md", borderRadius.md);
    root.style.setProperty("--radius-lg", borderRadius.lg);
    root.style.setProperty("--radius-xl", borderRadius.xl);

    // Animation (respect reduced motion)
    const motionMultiplier = prefersReducedMotion ? 0 : 1;
    root.style.setProperty(
      "--animation-fast",
      prefersReducedMotion ? "none" : animation.fast,
    );
    root.style.setProperty(
      "--animation-normal",
      prefersReducedMotion ? "none" : animation.normal,
    );
    root.style.setProperty(
      "--animation-slow",
      prefersReducedMotion ? "none" : animation.slow,
    );
    root.style.setProperty(
      "--duration-fast",
      `${animation.duration.fast * motionMultiplier}ms`,
    );
    root.style.setProperty(
      "--duration-normal",
      `${animation.duration.normal * motionMultiplier}ms`,
    );
    root.style.setProperty(
      "--duration-slow",
      `${animation.duration.slow * motionMultiplier}ms`,
    );

    // Focus
    root.style.setProperty("--focus-ring-color", focus.color);
    root.style.setProperty("--focus-ring-width", focus.width);
    root.style.setProperty("--focus-ring-offset", focus.offset);

    // Shadows
    root.style.setProperty("--shadow-sm", shadows.sm);
    root.style.setProperty("--shadow-md", shadows.md);
    root.style.setProperty("--shadow-lg", shadows.lg);
    root.style.setProperty("--shadow-xl", shadows.xl);

    // Apply base styles to body
    document.body.style.backgroundColor = colors.background.base;
    document.body.style.color = colors.text.primary;
    document.body.style.fontFamily = typography.fontFamily.primary;
  }, [tokens, theme, mode, prefersReducedMotion]);

  // ──────────────────────────────────────────────────────────────────────────
  // Scrollbar Styles — thin, elegant, theme-connected, fully accessible
  // ──────────────────────────────────────────────────────────────────────────
  const scrollbarCSS = useMemo(
    () => buildScrollbarCSS(tokens, mode, prefersReducedMotion),
    [tokens, mode, prefersReducedMotion],
  );

  const value: ThemeContextValue = useMemo(
    () => ({
      theme,
      mode,
      tokens,
      prefersReducedMotion,
      accentColor,
      setTheme,
      setMode,
      toggleMode,
      setAccentColor,
    }),
    [theme, mode, tokens, prefersReducedMotion, accentColor],
  );

  // Global reduced-motion rule — ensures ALL animations and transitions across
  // every Zendir component respect the OS preference (WCAG 2.3.3).
  // Uses 0.01ms (not 0) so animation-end events still fire correctly.
  const reducedMotionCSS = `@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}`;

  return (
    <ThemeContext.Provider value={value}>
      <style
        data-zendir-scrollbar=""
        dangerouslySetInnerHTML={{ __html: scrollbarCSS }}
      />
      <style
        data-zendir-reduced-motion=""
        dangerouslySetInnerHTML={{ __html: reducedMotionCSS }}
      />
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme - Access theme context
 *
 * @throws Error if used outside ThemeProvider
 *
 * @example
 * ```tsx
 * const { tokens, mode, toggleMode } = useTheme();
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

/**
 * useThemeTokens - Access only theme tokens (for memoization)
 */
export function useThemeTokens(): ThemeTokens {
  return useTheme().tokens;
}
